import { getCookie } from "cookies-next";
import { useEffect, useState } from "react";
import { v4 } from "uuid";
import Button from "../components/Button";
import FileInput from "../components/FileInput";
import { EAnalysesType } from "../oz/constants/ozConstant";
import { randomGesture } from "../oz/constants/ozLivenessConstant";
import {
  analysesFolderById,
  authenticateOz2,
  handleLivenessResult,
  handleSingleRequest,
  pollAnalysesById,
  uploadMediaToFolderById,
} from "../services/ozservices";

type TSectionName = "document" | "biometric" | "liveness";

const DocumentPage = () => {
  const [form, setForm] = useState({
    document1: null,
    document2: null,
    document3: null,
    photo: null,
  });

  const [loading, setLoading] = useState({
    document: false,
    biometric: false,
    liveness: false,
  });

  const [result, setResult] = useState({
    document: {
      message: "",
    },
    biometric: {
      message: "",
    },
    liveness: {
      message: "",
    },
  });

  useEffect(() => {
    if (!getCookie("access_token2")) {
      authenticateOz2();
    }
  }, []);

  const handleResultUpdate = ({
    name,
    message,
  }: {
    name: TSectionName;
    message: string;
  }) => {
    setResult((prevData) => ({
      ...prevData,
      [name]: { message },
    }));
  };

  const handleLoadingUpdate = ({
    name,
    loading,
  }: {
    name: TSectionName;
    loading: boolean;
  }) => {
    setLoading((prevData) => ({
      ...prevData,
      [name]: loading,
    }));
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;

    // Check if the input is a file input or a dropdown
    if (
      name === "document1" ||
      name === "photo" ||
      name === "document2" ||
      name === "document3"
    ) {
      if (files && files[0]) {
        setForm((prevForm) => ({
          ...prevForm,
          [name]: files[0], // Store the first file
        }));
      }
    } else if (name === "selectedOption") {
      setForm((prevForm) => ({
        ...prevForm,
        [name]: value, // Store the selected dropdown value
      }));
    }
  };

  const handleCheckDocument = async () => {
    try {
      handleLoadingUpdate({ name: "document", loading: true });
      handleResultUpdate({ name: "document", message: "" });
      const formData = new FormData();
      if (!form.document1) {
        return;
      }
      handleResultUpdate({ name: "document", message: "" });
      formData.append("photo1", form.document1);

      const payload = {
        "media:tags": {
          photo1: ["photo_id_front"],
        },
        analyses: [{ type: "documents" }],
      };

      formData.append("payload", JSON.stringify(payload));

      const response = await handleSingleRequest(formData);
      const message =
        response?.analyses[0].resolution_status === "SUCCESS"
          ? "Document Valid"
          : "Document Not Valid";
      if (message) {
        handleResultUpdate({ name: "document", message });
      }
    } catch (error) {
      console.error("Error Checking Document", error);
    } finally {
      handleLoadingUpdate({ name: "document", loading: false });
    }
  };

  const handleCheckBiometric = async () => {
    try {
      handleLoadingUpdate({ name: "biometric", loading: true });
      const formData = new FormData();
      if (!form.document2 || !form.photo) {
        return;
      }
      handleResultUpdate({ name: "document", message: "" });

      formData.append("photo1", form.photo);
      formData.append("document1", form.document2);

      const payload = {
        "media:tags": {
          photo1: ["photo_id"],
          document1: ["photo_id_front"],
        },
        analyses: [{ type: "biometry" }],
      };

      formData.append("payload", JSON.stringify(payload));

      const response = await handleSingleRequest(formData);
      const message =
        response?.analyses[0].resolution_status === "SUCCESS"
          ? "Face matched"
          : "Face did not match.";
      if (response) {
        handleResultUpdate({ name: "biometric", message });
      }
    } catch (error) {
      console.error("Error Checking Biometric", error);
    } finally {
      handleLoadingUpdate({ name: "biometric", loading: false });
    }
  };
  const handleLivenessChecking = async () => {
    try {
      handleLoadingUpdate({ name: "liveness", loading: true });
      console.log("button clicked");
      if (!form.document3) {
        console.log("return without checking");
        return;
      }
      console.log({ loading });
      const transactionId = v4();
      console.log({ transactionId });
      console.log({ form });

      await new Promise((resolve, reject) => {
        window.OzLiveness.open({
          lang: "en",
          action: [randomGesture()],
          meta: {
            user_id: "adbl",
            transaction_id: transactionId,
          },

          on_complete: async () => {
            try {
              const response = await handleLivenessResult({ transactionId });

              console.log({ response });

              const livenessResultResolution =
                response.items[0].analyses[0].resolution_status;

              const confidence_spoofing =
                response.items[0].analyses[0].results_media[0].results_data
                  .confidence_spoofing || "";

              console.log({ confidence_spoofing });

              const message =
                response.items[0].analyses[0].resolution_status === "SUCCESS"
                  ? "Live ness is Fine"
                  : "Attack Detected in Liveness";

              handleResultUpdate({ name: "liveness", message });
              if (!(livenessResultResolution === "SUCCESS")) {
                return resolve(undefined);
              }
              const folderId = response.items[0].folder_id;

              if (form.document3) {
                console.log("Document3 exists");
                const uploadFormData = new FormData();
                uploadFormData.append("photo1", form.document3);

                const payload = {
                  "media:tags": {
                    photo1: ["photo_id_front"],
                  },
                };
                uploadFormData.append("payload", JSON.stringify(payload));

                const uploadMediaResponse = await uploadMediaToFolderById({
                  folderId,
                  formData: uploadFormData,
                });

                console.log({ uploadMediaResponse });

                const analysesResponse = await analysesFolderById({
                  folderId,
                  data: {
                    analyses: [
                      {
                        type: EAnalysesType.BIOMETRY,
                      },
                    ],
                  },
                });

                const analysisId = analysesResponse?.[0].analyse_id;
                const analysesResponseState = analysesResponse?.[0].state;

                console.log({ analysesResponseState });

                if (analysisId && analysesResponseState === "PROCESSING") {
                  console.log("Analyses State Processing detected");
                  let pollState = "PROCESSING";
                  let pollAnalysesResponse;

                  while (pollState === "PROCESSING") {
                    console.log("Entering While Loop");
                    pollAnalysesResponse = await pollAnalysesById({
                      analysisId,
                    });
                    console.log({ pollAnalysesResponse });
                    pollState = pollAnalysesResponse.state;

                    if (pollState === "FINISHED") {
                      const resolution_status =
                        pollAnalysesResponse.resolution_status;
                      if (resolution_status === "DECLINED") {
                        handleResultUpdate({
                          name: "liveness",
                          message: "Face did not match.",
                        });
                      }
                      if (resolution_status === "SUCCESS") {
                        handleResultUpdate({
                          name: "liveness",
                          message: "Face matched.",
                        });
                      }
                    }
                    console.log({ pollState });

                    await new Promise((resolve) => setTimeout(resolve, 2000));
                  }
                  console.log("Exiting while loop");

                  // const resolution_status =
                  //   analysesResponse[0].resolution_status;
                  // console.log({ resolution_status });

                  // if (resolution_status === "DECLINED") {
                  //   handleResultUpdate({
                  //     name: "liveness",
                  //     message: "Face did not match.",
                  //   });
                  // } else {
                  //   handleResultUpdate({
                  //     name: "liveness",
                  //     message: "Face matched.",
                  //   });
                  // }
                }
              }
              resolve(undefined); // Success path
            } catch (error) {
              reject(error);
            }
          },
        });
      });
    } catch (error) {
      console.error("Error Checking Biometric and Liveness", error);
    } finally {
      handleLoadingUpdate({ name: "liveness", loading: false });
    }
  };

  return (
    <main className="flex justify-center items-center px-8 py-4 ">
      <div className="container p-4">
        <div className="flex flex-col gap-8 justify-center items-center">
          <h1 className="text-5xl">Biometric Demo</h1>

          <section className="w-full flex flex-col gap-4 bg-gray-100 p-4">
            <div>
              <h2 className="text-xl">Check Document</h2>
              <p className="text-sm text-gray-500">
                Check if valid document or not
              </p>
            </div>
            <FileInput
              name={"document1"}
              label="Document"
              value={form.document1}
              handleFormChange={handleFormChange}
            />
            <div className="flex gap-2">
              <Button disabled={loading.document} onClick={handleCheckDocument}>
                {loading.document ? "Loading..." : "Check Document"}
              </Button>
            </div>

            {result.document.message && (
              <div>
                <p className="text-xl">Result:</p>
                <p>{result.document.message}</p>
              </div>
            )}
          </section>

          <section className="w-full flex flex-col gap-4 bg-gray-100 p-4">
            <div>
              <h2 className="text-xl">Check Biometric</h2>
              <p className="text-sm text-gray-500">
                Check if person matches with document using existing photo
              </p>
            </div>
            <div className="flex flex-col gap-4">
              <FileInput
                name={"document2"}
                label="Document"
                value={form.document2}
                handleFormChange={handleFormChange}
              />

              <FileInput
                name={"photo"}
                label="Photo"
                value={form.photo}
                handleFormChange={handleFormChange}
              />
            </div>
            <div className="flex gap-2">
              <Button
                disabled={loading.biometric}
                onClick={handleCheckBiometric}
              >
                {loading.biometric ? "Loading..." : "Check Biometric"}
              </Button>
            </div>

            {result.biometric.message && (
              <div>
                <p className="text-xl">Result:</p>
                <p>{result.biometric.message}</p>
              </div>
            )}
          </section>

          <section className="w-full flex flex-col gap-4 bg-gray-100 p-4">
            <div>
              <h2 className="text-xl">Check Liveness and Document</h2>
              <p className="text-sm text-gray-500">
                Check if person match with document in image
              </p>
            </div>
            <FileInput
              name={"document3"}
              label="Document"
              value={form.document3}
              handleFormChange={handleFormChange}
            />
            <div className="flex gap-2">
              <Button
                disabled={loading.liveness}
                onClick={handleLivenessChecking}
              >
                {loading.liveness ? "Loading..." : "Start Checking"}
              </Button>
            </div>

            {result.liveness.message && (
              <div>
                <p className="text-xl">Result:</p>
                <p>{result.liveness.message}</p>
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
};

export default DocumentPage;
